import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { Job } from '../../types/work';
import { getRelativeTime } from '../../data/mockWorkData';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

const LIGHT_BORDER_GRADIENT: readonly [string, string, string] = [
  'rgba(255,255,255,0.85)',
  'rgba(185,185,185,0.55)',
  'rgba(245,245,245,0.8)',
];

const DARK_BORDER_GRADIENT: readonly [string, string, string] = [
  'rgba(255,255,255,0.55)',
  'rgba(120,120,120,0.35)',
  'rgba(235,235,235,0.45)',
];

interface JobCardProps {
  job: Job;
  onPress: () => void;
  showQuoteCount?: boolean;
  quoteCount?: number;
}

const JobCard: React.FC<JobCardProps> = ({ job, onPress, showQuoteCount = false, quoteCount = 0 }) => {
  const { theme, isDark } = useTheme();
  const gradientColors = isDark ? DARK_BORDER_GRADIENT : LIGHT_BORDER_GRADIENT;
  const budgetText = job.budget ? `$${job.budget.toLocaleString('en-IN')}` : 'Not disclosed';

  return (
    <TouchableOpacity
      style={[styles.cardWrapper, { shadowColor: theme.shadow }]}
      onPress={onPress}
      activeOpacity={0.9}
    >
      <LinearGradient
        colors={gradientColors}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.cardBorder}
      >
        <View
          style={[
            styles.cardInner,
            {
              backgroundColor: theme.card,
              borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            },
          ]}
        >
          <View
            style={[
              styles.categoryTag,
              {
                borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.96)',
              },
            ]}
          >
            <View style={[styles.categoryDot, { backgroundColor: theme.accent }]} />
            <ThemedText style={[styles.categoryTagText, { color: theme.accent }]} numberOfLines={1}>
              {job.category}
            </ThemedText>
          </View>

          <View style={styles.headerRow}>
            <View style={styles.headerMeta}>
              <View style={styles.timeContainer}>
                <Ionicons name="time-outline" size={14} color={theme.textTertiary} />
                <ThemedText style={[styles.timeText, { color: theme.textTertiary }]}>
                  {getRelativeTime(job.createdAt)}
                </ThemedText>
              </View>
            </View>
            {showQuoteCount && (
              <View
                style={[
                  styles.quoteBadge,
                  {
                    borderColor: theme.accent + '33',
                    backgroundColor: theme.accent + '12',
                  },
                ]}
              >
                <Ionicons name="chatbubbles-outline" size={14} color={theme.accent} />
                <ThemedText style={[styles.quoteText, { color: theme.accent }]}>
                  {quoteCount} {quoteCount === 1 ? 'Quote' : 'Quotes'}
                </ThemedText>
              </View>
            )}
          </View>

          <ThemedText style={[styles.title, { color: theme.text }]} numberOfLines={2}>
            {job.title}
          </ThemedText>

          <ThemedText style={[styles.description, { color: theme.textSecondary }]} numberOfLines={3}>
            {job.description}
          </ThemedText>

          <View style={[styles.metaStrip, { backgroundColor: theme.backgroundSecondary }]}>
            <View style={styles.metaItem}>
              <Ionicons name="cash-outline" size={16} color={theme.accent} />
              <View>
                <ThemedText style={[styles.metaLabel, { color: theme.textSecondary }]}>Budget</ThemedText>
                <ThemedText style={[styles.metaValue, { color: theme.accent }]}>{budgetText}</ThemedText>
              </View>
            </View>
            <View style={[styles.metaDivider, { backgroundColor: theme.border }]} />
            <View style={styles.metaItem}>
              <Ionicons
                name={
                  job.type === 'contract' ? 'time-outline' : 
                  job.type === 'supply' ? 'cube-outline' : 
                  'pricetag-outline'
                }
                size={16}
                color={theme.accent}
              />
              <View>
                <ThemedText style={[styles.metaLabel, { color: theme.textSecondary }]}>
                  {job.type === 'contract' ? 'Duration' : 'Type'}
                </ThemedText>
                <ThemedText style={[styles.metaValue, { color: theme.text }]}>
                  {job.type === 'contract' 
                    ? (job.duration || 'Not specified')
                    : job.type === 'supply' 
                      ? 'Requirement' 
                      : job.type.charAt(0).toUpperCase() + job.type.slice(1)
                  }
                </ThemedText>
              </View>
            </View>
          </View>
        </View>
      </LinearGradient>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  cardWrapper: {
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 20,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 5,
  },
  cardBorder: {
    borderRadius: 20,
    padding: 1,
  },
  cardInner: {
    borderRadius: 18,
    borderWidth: 1,
    padding: 20,
    paddingTop: 36,
    position: 'relative',
  },
  categoryTag: {
    position: 'absolute',
    top: 0,
    right: 0,
    borderBottomLeftRadius: 18,
    borderTopRightRadius: 18,
    borderWidth: 1,
    borderLeftWidth: 0,
    borderBottomWidth: 0,
    paddingHorizontal: 14,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  categoryDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  categoryTagText: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    maxWidth: 120,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
    gap: 12,
  },
  headerMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  quoteBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    gap: 6,
  },
  quoteText: {
    fontSize: 12,
    fontWeight: '700',
  },
  timeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  timeText: {
    fontSize: 12,
    fontWeight: '600',
  },
  title: {
    fontSize: 20,
    fontWeight: '800',
    lineHeight: 28,
    letterSpacing: -0.3,
    marginBottom: 6,
  },
  description: {
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 18,
  },
  metaStrip: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 16,
    padding: 14,
    gap: 16,
  },
  metaItem: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  metaLabel: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 2,
  },
  metaValue: {
    fontSize: 16,
    fontWeight: '700',
  },
  metaDivider: {
    width: 1,
    alignSelf: 'stretch',
    borderRadius: 999,
    opacity: 0.5,
  },
});

export default JobCard;
